import React from 'react';
import { reportError } from '../../utils/reportError';

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Rendered instead of `children` once an error is caught. */
  fallback: (error: Error, reset: () => void) => React.ReactNode;
  /** Label passed through to reportError so we can tell boundaries apart. */
  source?: string;
  onError?: (error: Error, componentStack: string) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render-time crashes so one broken component degrades to an error card
 * instead of unmounting the whole React tree (the classic blank white page).
 *
 * IMPORTANT — what this does NOT catch:
 *   - errors thrown inside event handlers (onClick, onSubmit, ...)
 *   - errors inside async code: promises, setTimeout, thunks
 *   - errors thrown during server-side rendering
 * For those, keep using try/catch plus a toast. Error boundaries only cover
 * render, lifecycle methods, and constructors of the tree beneath them.
 *
 * NOTE for development: under <StrictMode> React re-throws caught errors to the
 * console after the boundary handles them. Seeing the error logged does not mean
 * the boundary failed — the fallback UI rendering is the proof that it worked.
 *
 * This must stay a class component: React exposes no hook equivalent of
 * getDerivedStateFromError / componentDidCatch.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    reportError(error, {
      source: this.props.source ?? 'error-boundary',
      componentStack: errorInfo.componentStack ?? undefined,
    });
    this.props.onError?.(error, errorInfo.componentStack ?? '');
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    const { error } = this.state;

    if (error) {
      return this.props.fallback(error, this.reset);
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
